import { c as _c } from "react/compiler-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDilutionLabel, formatDilutionStrength } from "@/lib/dilution";
import { cx } from "@/lib/tailwind-utils";
import type { MedicationDilution } from "@/types/medication";
interface DilutionPickerProps {
  id: string;
  dilutions: MedicationDilution[];
  value: string | null | undefined;
  onChange: (dilutionId: string) => void;
  /** The medication's unit, for the strength line ("4 U / 0.1 mL"). */
  unit?: string | null;
  className?: string;
  /** For hosts that stack above the default popover layer. */
  contentClassName?: string;
}

/**
 * A dropdown of reconstitutions; a row of chips overflows narrow toolbars once a
 * medication has more than a few. A medication with a single dilution, already
 * picked, has nothing to choose, so it reads as text.
 */
export function DilutionPicker(t0) {
  const $ = _c(30);
  const {
    id,
    dilutions,
    value,
    onChange,
    unit,
    className,
    contentClassName
  } = t0;
  if (dilutions.length === 1 && dilutions[0].id === value) {
    const [only] = dilutions;
    let t1;
    if ($[0] !== only || $[1] !== unit) {
      t1 = formatDilutionStrength(only, unit);
      $[0] = only;
      $[1] = unit;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const strength = t1;
    let t2;
    if ($[3] !== only.diluentVolume) {
      t2 = formatDilutionLabel(only.diluentVolume);
      $[3] = only.diluentVolume;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== strength) {
      t3 = strength && <span className="text-foreground-muted"> · {strength}</span>;
      $[5] = strength;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== id || $[8] !== t2 || $[9] !== t3) {
      t4 = <p id={id} className="text-body text-foreground tabular-nums">{t2}{t3}</p>;
      $[7] = id;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  const t1 = value ?? "";
  let t2;
  if ($[11] !== className) {
    t2 = cx("w-auto min-w-[9rem] tabular-nums", className);
    $[11] = className;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectValue placeholder="Reconstitution" />;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== id || $[15] !== t2) {
    t4 = <SelectTrigger id={id} aria-label="Reconstitution" className={t2}>{t3}</SelectTrigger>;
    $[14] = id;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== dilutions || $[18] !== unit) {
    let t6;
    if ($[20] !== unit) {
      t6 = dilution => {
        if (!dilution.id) {
          return null;
        }
        const strength_0 = formatDilutionStrength(dilution, unit);
        return <SelectItem key={dilution.id} value={dilution.id} className="tabular-nums">{formatDilutionLabel(dilution.diluentVolume)}{strength_0 && <span className="font-normal text-foreground-muted">{" "}· {strength_0}</span>}</SelectItem>;
      };
      $[20] = unit;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    t5 = dilutions.map(t6);
    $[17] = dilutions;
    $[18] = unit;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[22] !== contentClassName || $[23] !== t5) {
    t6 = <SelectContent className={contentClassName}>{t5}</SelectContent>;
    $[22] = contentClassName;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== onChange || $[26] !== t1 || $[27] !== t4 || $[28] !== t6) {
    t7 = <Select value={t1} onValueChange={onChange}>{t4}{t6}</Select>;
    $[25] = onChange;
    $[26] = t1;
    $[27] = t4;
    $[28] = t6;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  return t7;
}